import cn from 'classnames';
import { isFirstDayOfMonth } from 'date-fns';
import * as React from 'react';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';

import { CdnIcon } from '@shared/ui/CdnIcon';
import { RouteType, getProgressLevel } from '@features/price-graph';
import { Money } from '../../../../../../Money/types';
import { Money as MoneyComponent } from '../../../../../../Money/components/Money';
import { Tooltip } from '../../../../../../BaseComponents';
import { format, TABLET_MIN_WIDTH, removeDotsFromDate } from '../../../../../../utils';
import { useTheme } from '../../../../../../theme';

export interface DayProps {
	date: Date;
	price: Money;
	isOutDate: boolean;
	isBackDate: boolean;
	routeType: RouteType;
}

export interface AdditionalDayProps {
	onSelect?: (date: Date, isBackDate?: boolean) => void;
	minPrice: Money;
	maxPrice: Money;
	isFocused?: boolean;
	isMobileMode?: boolean;
}

const Day: React.FC<DayProps & AdditionalDayProps> = ({
	date,
	price,
	maxPrice,
	minPrice,
	routeType,
	isBackDate,
	isOutDate,
	isFocused,
	isMobileMode,
	onSelect
}) => {
	const { t } = useTranslation('PriceGraph');
	const { DayStyles: theme } = useTheme('Features').PriceGraph;

	const day = date.getDate(),
		dayOfWeek = format(date, 'eeeee'),
		progress = price && maxPrice ? getProgressLevel(price.amount, maxPrice.amount) : 0;

	const handleClick = () => {
		if (isMobileMode) {
			onSelect?.(date, routeType === RouteType.Return);
		} else if (price?.amount) {
			onSelect?.(date, routeType === RouteType.Return);
		}
	};

	const minPriceDay = minPrice && price && price.amount === minPrice.amount && price.amount;

	return (
		<div
			className={cn(theme.day, {
				[theme.day_notAvailable]: !price || !price.amount,
				[theme.outDate]: isOutDate,
				[theme.backDate]: isBackDate,
				[theme.day_focused]: isFocused,
				[theme.minPrice]: minPriceDay
			})}
			onClick={handleClick}
		>
			{price.amount > 0 && (
				<div className={theme.price}>
					{t('from')}
					<MoneyComponent money={price} />
				</div>
			)}

			{isFirstDayOfMonth(date) && <div className={theme.monthName}>{format(date, 'LLLL')}</div>}

			<MediaQuery minWidth={TABLET_MIN_WIDTH + 1}>
				<Tooltip
					disableHoverListener={!price.amount}
					title={
						<div className={theme.tooltip__text}>
							<CdnIcon
								className={cn({
									[theme.tooltip__icon_return]: routeType === RouteType.Return
								})}
								icon={'Flight/Direction'}
							/>
							<div className={theme.tooltip__text_box}>
								<div className={theme.tooltip__date}>
									{removeDotsFromDate(format(date, 'd MMM, EEEEEE'))}
								</div>
								<div className={theme.tooltip__price}>
									{t('from')} <MoneyComponent moneyClassName={theme.tooltip__money} money={price} />
								</div>
							</div>
						</div>
					}
					placement="bottom"
					classes={{
						tooltip: theme.tooltip,
						popper: theme.popper
					}}
					arrow={false}
				>
					<div className={theme.bar}>
						<div
							className={theme.progress}
							style={price && price.amount ? { height: `${progress}%` } : null}
						/>
					</div>
				</Tooltip>
			</MediaQuery>

			<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
				<div className={theme.bar}>
					<div className={theme.progress} style={price.amount ? { height: `${progress}%` } : null} />
				</div>
			</MediaQuery>

			<div className={theme.header}>
				<div
					className={cn(theme.number, {
						[theme.outDate]: isOutDate,
						[theme.backDate]: isBackDate
					})}
				>
					{day}
				</div>
				<div className={theme.dow}>{dayOfWeek}</div>
			</div>
		</div>
	);
};
export default Day;
